import b from 'b_';
import { Help, Text } from 'platform-components';
import { HELP_ACTION, POPUP_DIRECTIONS, POPUP_THEMES, HELP_TYPE } from 'platform-components/constants';
import React, { useState, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

// @ts-ignore
import { IconArrow } from 'common.components/icon/icon';
import { actions as sidePanelActions } from 'common.components/lesson-view.side-panel/actions';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';

import LessonStats from './__lesson-stats/stats__lesson-stats';
import ProblemStats from './__problem-stats/stats__problem-stats';
import StudentStats from './__student-stats/stats__student-stats';
import * as selectors from './selectors';
import { STATS_MODE } from './typings';

import './stats.scss';

interface StoreProps {
    mode: STATS_MODE;
}

interface DispatchProps {
    setExpanded: typeof sidePanelActions.setExpanded;
}

interface Props extends StoreProps, DispatchProps {}

export const Stats = ({ mode, setExpanded }: Props) => {
    const [areStatsHidden, setStatsOpened] = useState(true);
    const isHeadless = useSelector(lessonViewSelectors.getIsHeadless);

    const helpProps = useMemo(
        () => ({
            mix: 'stats__help',
            popupProps: {
                theme: POPUP_THEMES.SCHOOL,
                direction: isHeadless ? POPUP_DIRECTIONS.BOTTOM : POPUP_DIRECTIONS.RIGHT,
                hasArrow: true,
                inLayer: true,
            },
            showPopupOnAction: HELP_ACTION.HOVER,
            type: HELP_TYPE.INFO,
        }),
        [isHeadless]
    );

    const arrowDirection = areStatsHidden ? IconArrow.DIRECTIONS.BOTTOM : IconArrow.DIRECTIONS.TOP;

    const handleStatsExpanded = () => {
        setStatsOpened(!areStatsHidden);
        setExpanded(areStatsHidden); // флаги инвертированы
    };

    const renderContent = (): React.ReactElement | null => {
        switch (mode) {
            case STATS_MODE.LESSON:
                return <LessonStats />;
            case STATS_MODE.PROBLEM:
                return <ProblemStats />;
            case STATS_MODE.STUDENT:
                return <StudentStats />;
            default:
                return null;
        }
    };

    return (
        <div className="stats">
            <div className="stats__header">
                <Text id="lessonView.stats.header" size={Text.SIZE.XL} values={{ mode }} weight={Text.WEIGHT.M} />
                <Help {...helpProps}>
                    <Text id="lessonView.stats.header.info" />
                </Help>
            </div>
            <div
                className={b('stats', 'toggle-stats-control', { hidden: areStatsHidden })}
                {...accessibleClick(handleStatsExpanded)}
            >
                <IconArrow direction={arrowDirection} mix="stats__toggle-stats-control-arrow" />
                <Text id="lessonView.stats.header" values={{ mode }} weight={Text.WEIGHT.M} />
            </div>
            <div className={b('stats', 'stats-wrapper', { hidden: areStatsHidden })}>{renderContent()}</div>
        </div>
    );
};

const mapStateToProps = (storeState: any): StoreProps => {
    return {
        mode: selectors.getStatsMode(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            setExpanded: sidePanelActions.setExpanded,
        },
        dispatch
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Stats);
